/* eslint-disable max-lines */
/**
 * Editor find & replace hook (#2233).
 *
 * Doc-shape agnostic — the caller passes a flat list of `{ key, text }`
 * segments (one per utterance / caption / paragraph). The hook does the
 * matching (case-sensitive, diacritics-insensitive, whole-word), holds
 * the query / replacement / current-index state, and returns matches
 * with enough context for the popover to render a match list.
 *
 * The hook never mutates the document. `applyReplaceAll(segments)`
 * returns a fresh list of segments with the replacements applied, and
 * `applyReplaceOne(segments, matchId)` returns the same for a single
 * match. The caller applies the returned list to their own store.
 */

import { useCallback, useMemo, useState } from 'react';

export interface FindReplaceSegment {
  /** Stable id used to route matches back to the editor. */
  key: string;
  text: string;
}

export interface FindReplaceOptions {
  caseSensitive: boolean;
  ignoreDiacritics: boolean;
  wholeWord: boolean;
}

export interface FindReplaceMatch {
  /** Stable per-match id: `${segmentKey}:${from}-${to}` */
  id: string;
  segmentKey: string;
  segmentIndex: number;
  /** Char offset within the segment (raw text, no normalisation). */
  from: number;
  to: number;
  /** Exact substring in the segment (original casing / diacritics). */
  text: string;
  /** Up to `CONTEXT_CHARS` before the match, from the same segment. */
  contextBefore: string;
  /** Up to `CONTEXT_CHARS` after the match, from the same segment. */
  contextAfter: string;
}

const CONTEXT_CHARS = 40;

/**
 * Build the "before" context by walking left through neighbouring
 * segments until we have ~CONTEXT_CHARS worth of characters. The
 * transcript editor passes one segment per word (one Slate text node),
 * so slicing the current segment alone almost always returns an empty
 * string — this walk is what makes the result rows readable.
 */
function collectContextBefore(
  segments: readonly FindReplaceSegment[],
  segIndex: number,
  from: number
): string {
  const parts: string[] = [];
  const head = segments[segIndex].text
    .slice(Math.max(0, from - CONTEXT_CHARS), from)
    .replace(/\s+/g, ' ');
  let budget = CONTEXT_CHARS - head.length;
  for (let i = segIndex - 1; i >= 0 && budget > 0; i--) {
    const t = segments[i].text.replace(/\s+/g, ' ');
    if (!t) continue;
    const take = t.slice(Math.max(0, t.length - budget));
    parts.unshift(take);
    budget -= take.length;
  }
  const joined = parts.join(' ').trim();
  if (!joined) return head;
  const spacer = head.startsWith(' ') || head === '' ? '' : ' ';
  return `${joined}${spacer}${head}`;
}

function collectContextAfter(
  segments: readonly FindReplaceSegment[],
  segIndex: number,
  to: number
): string {
  const tail = segments[segIndex].text
    .slice(to, Math.min(segments[segIndex].text.length, to + CONTEXT_CHARS))
    .replace(/\s+/g, ' ');
  const parts: string[] = [tail];
  let budget = CONTEXT_CHARS - tail.length;
  for (let i = segIndex + 1; i < segments.length && budget > 0; i++) {
    const t = segments[i].text.replace(/\s+/g, ' ');
    if (!t) continue;
    const take = t.slice(0, budget);
    parts.push(take);
    budget -= take.length;
  }
  return parts.join(' ').trim().length > tail.trim().length
    ? parts
        .map(p => p.trim())
        .filter(Boolean)
        .join(' ')
    : tail;
}

/** Strip combining marks — `t̆éner` → `tener`. */
function stripDiacritics(input: string): string {
  return input.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Escape regex metacharacters in user-supplied query text. */
function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * If the original match started with an uppercase letter, uppercase the
 * first letter of the replacement. Preserves accented uppercase forms
 * (`É` → `É`) because `toLocaleUpperCase` handles them.
 */
export function preserveFirstLetterCapitalisation(
  matchText: string,
  replacement: string,
  enabled: boolean
): string {
  if (!enabled || !matchText || !replacement) return replacement;
  const firstChar = matchText.at(0);
  if (!firstChar) return replacement;
  const isUpper =
    firstChar === firstChar.toLocaleUpperCase() &&
    firstChar !== firstChar.toLocaleLowerCase();
  if (!isUpper) return replacement;
  const first = replacement.at(0);
  if (!first) return replacement;
  return first.toLocaleUpperCase() + replacement.slice(1);
}

/**
 * Compute matches for a single segment. Exported for tests; the hook
 * runs it across all segments.
 */
export function findMatchesInText(
  text: string,
  query: string,
  options: FindReplaceOptions
): Array<Pick<FindReplaceMatch, 'from' | 'to' | 'text'>> {
  if (!query || !text) return [];
  const haystack = options.ignoreDiacritics ? stripDiacritics(text) : text;
  const needleRaw = options.ignoreDiacritics ? stripDiacritics(query) : query;
  if (!needleRaw) return [];
  const escapedNeedle = escapeRegex(needleRaw);
  const pattern = options.wholeWord ? `\\b${escapedNeedle}\\b` : escapedNeedle;
  let rx: RegExp;
  try {
    rx = new RegExp(pattern, options.caseSensitive ? 'g' : 'gi');
  } catch {
    return [];
  }

  const out: Array<Pick<FindReplaceMatch, 'from' | 'to' | 'text'>> = [];
  let match: RegExpExecArray | null;
  // Guard against zero-width matches locking the loop.
  while ((match = rx.exec(haystack)) !== null) {
    const from = match.index;
    const to = match.index + match[0].length;
    if (to === from) {
      rx.lastIndex += 1;
      continue;
    }
    // The `to`/`from` refer to indices in `haystack`, which — when
    // `ignoreDiacritics` is on — has the SAME LENGTH as `text` because
    // NFD-then-strip only removes combining marks. Combining marks are
    // separate code points that follow the base letter, so mapping is
    // preserved.
    //
    // If future normalisation forms change the length (e.g. NFC folding
    // `é` → `é` isn't a length change but NFKC can be), the
    // mapping breaks. For now keep the raw index.
    out.push({ from, to, text: text.slice(from, to) });
  }
  return out;
}

interface UseFindReplaceInput {
  segments: FindReplaceSegment[];
  /** Optional initial query — useful when opening the popover with a selection. */
  initialQuery?: string;
}

export interface UseFindReplaceResult {
  query: string;
  setQuery: (q: string) => void;
  replacement: string;
  setReplacement: (r: string) => void;
  options: FindReplaceOptions;
  setOption: <K extends keyof FindReplaceOptions>(
    key: K,
    value: FindReplaceOptions[K]
  ) => void;
  preserveFirstLetterCase: boolean;
  setPreserveFirstLetterCase: (v: boolean) => void;
  matches: FindReplaceMatch[];
  currentIndex: number;
  current: FindReplaceMatch | null;
  next: () => void;
  prev: () => void;
  jumpTo: (matchIndex: number) => void;
  /** Segments with the *current* match replaced. */
  applyReplaceCurrent: (
    segments: FindReplaceSegment[]
  ) => { segments: FindReplaceSegment[]; replacedText: string } | null;
  /** Segments with all matches replaced. Returns how many were replaced. */
  applyReplaceAll: (segments: FindReplaceSegment[]) => {
    segments: FindReplaceSegment[];
    replacedCount: number;
  };
  reset: () => void;
}

export function useFindReplace({
  segments,
  initialQuery = ''
}: UseFindReplaceInput): UseFindReplaceResult {
  const [query, setQuery] = useState<string>(initialQuery);
  const [replacement, setReplacement] = useState<string>('');
  const [options, setOptions] = useState<FindReplaceOptions>({
    caseSensitive: false,
    ignoreDiacritics: true,
    wholeWord: false
  });
  const [preserveFirstLetterCase, setPreserveFirstLetterCase] =
    useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const setOption = useCallback(
    <K extends keyof FindReplaceOptions>(
      key: K,
      value: FindReplaceOptions[K]
    ) => setOptions(prev => ({ ...prev, [key]: value })),
    []
  );

  const matches = useMemo<FindReplaceMatch[]>(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const out: FindReplaceMatch[] = [];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const hits = findMatchesInText(seg.text, trimmed, options);
      for (const h of hits) {
        out.push({
          id: `${seg.key}:${h.from}-${h.to}`,
          segmentKey: seg.key,
          segmentIndex: i,
          from: h.from,
          to: h.to,
          text: h.text,
          // Pull context from neighbouring segments too — transcripts
          // split one Slate text node per word, so slicing within a
          // segment yields empty surroundings. #2290: result rows were
          // showing the bare match with nothing around it. Walking the
          // segment array in both directions until we have ~CONTEXT_CHARS
          // worth of chars rebuilds the surrounding words, no matter
          // whether a "segment" is a caption line (20+ chars) or a
          // single transcript word (3-8 chars).
          contextBefore: collectContextBefore(segments, i, h.from),
          contextAfter: collectContextAfter(segments, i, h.to)
        });
      }
    }
    return out;
  }, [segments, query, options]);

  // Keep `currentIndex` inside the valid range as matches change.
  const boundedIndex = matches.length
    ? Math.max(0, Math.min(currentIndex, matches.length - 1))
    : 0;
  const current = matches[boundedIndex] ?? null;

  const next = useCallback(() => {
    if (!matches.length) return;
    setCurrentIndex(i => (i + 1) % matches.length);
  }, [matches.length]);

  const prev = useCallback(() => {
    if (!matches.length) return;
    setCurrentIndex(i => (i - 1 + matches.length) % matches.length);
  }, [matches.length]);

  const jumpTo = useCallback(
    (matchIndex: number) => {
      if (!matches.length) return;
      const clamped = Math.max(0, Math.min(matchIndex, matches.length - 1));
      setCurrentIndex(clamped);
    },
    [matches.length]
  );

  const applyReplaceCurrent = useCallback(
    (source: FindReplaceSegment[]) => {
      const m = matches[boundedIndex];
      if (!m) return null;
      const segIdx = source.findIndex(s => s.key === m.segmentKey);
      if (segIdx < 0) return null;
      const seg = source[segIdx];
      const finalReplacement = preserveFirstLetterCapitalisation(
        m.text,
        replacement,
        preserveFirstLetterCase
      );
      const newText =
        seg.text.slice(0, m.from) + finalReplacement + seg.text.slice(m.to);
      const nextSegments = source.map((s, i) =>
        i === segIdx ? { ...s, text: newText } : s
      );
      return { segments: nextSegments, replacedText: finalReplacement };
    },
    [matches, boundedIndex, replacement, preserveFirstLetterCase]
  );

  const applyReplaceAll = useCallback(
    (source: FindReplaceSegment[]) => {
      const bySegment = new Map<string, FindReplaceMatch[]>();
      for (const m of matches) {
        const list = bySegment.get(m.segmentKey) ?? [];
        list.push(m);
        bySegment.set(m.segmentKey, list);
      }
      let replacedCount = 0;
      const nextSegments = source.map(seg => {
        const segMatches = bySegment.get(seg.key);
        if (!segMatches || segMatches.length === 0) return seg;
        // Replace from right to left so earlier offsets stay valid.
        const ordered = [...segMatches].sort((a, b) => b.from - a.from);
        let text = seg.text;
        for (const m of ordered) {
          const finalReplacement = preserveFirstLetterCapitalisation(
            m.text,
            replacement,
            preserveFirstLetterCase
          );
          text = text.slice(0, m.from) + finalReplacement + text.slice(m.to);
          replacedCount += 1;
        }
        return { ...seg, text };
      });
      return { segments: nextSegments, replacedCount };
    },
    [matches, replacement, preserveFirstLetterCase]
  );

  const reset = useCallback(() => {
    setQuery('');
    setReplacement('');
    setCurrentIndex(0);
  }, []);

  return {
    query,
    setQuery,
    replacement,
    setReplacement,
    options,
    setOption,
    preserveFirstLetterCase,
    setPreserveFirstLetterCase,
    matches,
    currentIndex: boundedIndex,
    current,
    next,
    prev,
    jumpTo,
    applyReplaceCurrent,
    applyReplaceAll,
    reset
  };
}
